import { postsActions } from '../../../actions/posts/posts'
import { formatPostData } from './format-post-data'

/**
 * Fetch and format posts
 * @param {Object} params - Query parameters including assetId
 * @returns {Promise<Object>} Formatted posts with pagination info
 */
export const fetchPosts = async (params = {}) => {
  try {
    const { assetId, ...otherParams } = params;
    
    // Get posts from API
    const result = await postsActions.getPosts(assetId, otherParams)
    
    // Validate the response structure
    if (!result) {
      return { detail: [], totalCount: 0, pageCount: 0 }
    }
    
    // Handle different response formats
    let detail = []
    if (Array.isArray(result)) {
      detail = result
    } else if (result.detail && Array.isArray(result.detail)) {
      detail = result.detail
    } else if (result.items && Array.isArray(result.items)) {
      detail = result.items
    }
    
    // Format each post
    const formattedPosts = detail.filter(
      (item) => item && item.message && item.message.trim() !== '',
    );
    
    // Return formatted data with pagination info
    return {
      detail: formattedPosts,
      totalCount: result.totalCount || result.total || 0,
      pageCount: result.pageCount || Math.ceil((result.total || 0) / 10) || 0,
    }
  } catch (error) {
    console.error('Error in fetchPosts service:', error)
    return { detail: [], totalCount: 0, pageCount: 0 }
  }
}

/**
 * Fetch and format single post detail
 * @param {string|number} postId - Post ID
 * @param {string} [assetId] - Asset ID (optional)
 * @returns {Promise<Object>} Formatted post detail
 */
export const fetchPostDetail = async (postId, assetId) => {
  // Convert postId to string for consistency
  if (!postId) {
    console.error('fetchPostDetail received null or undefined or empty postId')
    return null
  }
  
  if (typeof postId === 'object') {
    if (postId?.__v_isRef) {
      // Extract from Vue ref
      postId = postId.value
    } else {
      postId = postId?.id || postId?.postId
    }
  }
  
  // Check again after extraction
  if (!postId) {
    console.error('fetchPostDetail postId is still invalid after processing')
    return null
  }
  
  // Ensure postId is a string for URL parameter
  postId = String(postId)
  try {
    // Get post detail from API
    const response = await postsActions.getPostDetail(postId, assetId)
    
    // If response is an array, get the first element
    const post = Array.isArray(response) && response.length > 0 ? 
      response[0] : response;
    
    // If no post data found, return null
    if (!post) {
      console.warn('No post data found for ID:', postId);
      return null;
    }
    
    // Ensure platforms is properly formatted
    if (post.platforms && typeof post.platforms === 'string') {
      try {
        post.platforms = JSON.parse(post.platforms);
      } catch (e) {
        console.error('Error parsing platforms:', e);
        post.platforms = [];
      }
    }
    
    // Format the post data
    return post;
  } catch (error) {
    console.error('Error in fetchPostDetail service:', error)
    return null
  }
}
